import {
	GeoNode,
	GeoNodeType,
} from 'domain/subscription/interfaces/Subscription';

export const extractLeafNodes = (node: GeoNode): GeoNode[] => {
	if (!node.children || node.children.length === 0) {
		return [node];
	}
	return node.children.flatMap(extractLeafNodes);
};

export const areAllLeavesSelected = (
	node: GeoNode,
	selectedCodes: Set<string>,
): boolean => {
	const allLeaves = extractLeafNodes(node).map((leaf) => leaf.code);
	return allLeaves.every((leaf) => selectedCodes.has(leaf));
};

/**
 * Collapses complete branches to their parent nodes when all children are selected.
 * Returns the minimal set of nodes that represent the given node codes.
 */
export const collapseCompleteBranchesToParents = (
	areas: GeoNode[],
	countryCodes: string[],
): GeoNode[] => {
	if (!areas || !countryCodes || countryCodes.length === 0) {
		return [];
	}

	const selectedCodesSet = new Set(countryCodes);
	const result: GeoNode[] = [];

	const processNode = (node: GeoNode): boolean => {
		if (areAllLeavesSelected(node, selectedCodesSet)) {
			result.push(node);

			return true;
		}

		if (node.children && node.children.length > 0) {
			let anyChildProcessed = false;
			for (const child of node.children) {
				if (processNode(child)) {
					anyChildProcessed = true;
				}
			}
			return anyChildProcessed;
		}

		if (selectedCodesSet.has(node.code)) {
			result.push(node);
			return true;
		}

		return false;
	};

	areas.forEach((area) => processNode(area));

	return result;
};

export const geoNodeSort = (a: GeoNode, b: GeoNode): number => {
	if (a.nodeType === GeoNodeType.Continent) return -1;
	if (b.nodeType === GeoNodeType.Continent) return 1;

	return a.name.localeCompare(b.name);
};
